import { nanoid } from 'nanoid'

import {
  BusinessUnitTiny,
  CareerPathDto,
  Country,
  EmployeeDisplayTinyInfo,
  PyramidForecastDto,
  PyramidForecastInput,
  PyramidForecastWithFiltersDto,
} from '../../../types'
import {
  ForecastModalFilterValues,
  ForecastModalNewHiring,
  IForecastModalEmployeeTableForm,
  ForecastModalEmployeeTableFields,
  IForecastModalEmployeeTableField,
} from '.'

export interface IForecastModalData {
  pyramidForecastResponse: PyramidForecastDto[]
  employeeData: PyramidForecastDto[]
  newHiringsData: ForecastModalNewHiring[]
  employeeTableFormValues: IForecastModalEmployeeTableForm
  filterEmployees: EmployeeDisplayTinyInfo[]
  filterBu: BusinessUnitTiny[]
  careerPath: CareerPathDto[] | null
}

export const compareHiringsHash = (oldHiring: PyramidForecastDto, newHiring: ForecastModalNewHiring): boolean => {
  const oldHiringHash = JSON.stringify(
    `${oldHiring.newHiringFullName}
    ${oldHiring.businessUnitId}
    ${oldHiring.salary}
    ${oldHiring.bonus}
    ${oldHiring.age}
    ${oldHiring.levelChange}
    ${oldHiring.dateOfLevelChange}`,
  )
  const newHiringHash = JSON.stringify(
    `${newHiring.newHiringFullName}
    ${newHiring.businessUnitId}
    ${newHiring.salary}
    ${newHiring.bonus}
    ${newHiring.age}
    ${newHiring.levelChange}
    ${newHiring.dateOfLevelChange}`,
  )
  return oldHiringHash === newHiringHash
}

export const transformForecastModalData = (data: PyramidForecastWithFiltersDto): IForecastModalData => {
  const forecastModalData: IForecastModalData = {
    pyramidForecastResponse: [],
    employeeData: [],
    newHiringsData: [],
    employeeTableFormValues: {},
    filterEmployees: [],
    filterBu: [],
    careerPath: null,
  }

  if (!data) {
    return forecastModalData
  }

  if (!!data.activeEmployees && !!data.activeEmployees.length) {
    data.activeEmployees.forEach((employee: PyramidForecastDto) => {
      forecastModalData.employeeData.push({ ...employee })
      forecastModalData.pyramidForecastResponse.push({ ...employee })
      forecastModalData.employeeTableFormValues[employee.employeeId] = {
        currentValue: {
          levelChange: employee.levelChange || null,
          forecastDate: employee.forecastDate || null,
          forecastEndDate: employee.forecastEndDate || null,
        },
        pastValue: {
          levelChange: employee.levelChange || null,
          forecastDate: employee.forecastDate || null,
          forecastEndDate: employee.forecastEndDate || null,
        },
        lastModifiedField: '',
        hasErrors: '',
      }
    })
  }

  if (!!data.newHirings && !!data.newHirings.length) {
    data.newHirings.forEach((newHiring: PyramidForecastDto) => {
      const { id, newHiringFullName, businessUnitId, salary, bonus, age, levelChange, dateOfLevelChange } = newHiring
      forecastModalData.pyramidForecastResponse.push({ ...newHiring })
      forecastModalData.newHiringsData.push({
        id,
        newHiringFullName,
        businessUnitId,
        salary,
        bonus,
        age,
        levelChange,
        dateOfLevelChange,
        key: nanoid(),
        isNewHiring: false,
      })
    })
  }

  if (!!data.filters) {
    forecastModalData.filterEmployees = data.filters.employees || []
    forecastModalData.filterBu = data.filters.businessUnits || []
  }

  forecastModalData.careerPath = data.careerPath || null

  return forecastModalData
}

export const transformPyramidForecastData = (
  companyId: string,
  country: Country,
  employeeData: PyramidForecastDto[],
  hiringsData: ForecastModalNewHiring[],
  employeeTableFormValues: IForecastModalEmployeeTableForm,
  deletedHirings: string[],
): PyramidForecastInput[] => {
  let pyramidForecastInput: PyramidForecastInput[] = employeeData.reduce((acc: PyramidForecastInput[], employee: PyramidForecastDto) => {
    if (!!employee.employeeId) {
      const currentLevelChange = employeeTableFormValues[employee.employeeId].currentValue.levelChange
      const currentForecastDateChange = employeeTableFormValues[employee.employeeId].currentValue.forecastDate
      const currentForecastEndDateChange = employeeTableFormValues[employee.employeeId].currentValue.forecastEndDate
      if (
        currentLevelChange !== employee.levelChange ||
        currentForecastDateChange !== employee.forecastDate ||
        currentForecastEndDateChange !== employee.forecastEndDate
      ) {
        acc.push({
          companyId,
          country,
          id: employee.id,
          employeeId: employee.employeeId,
          forecastId: employee.forecastId,
          levelChange: employeeTableFormValues[employee.employeeId].currentValue.levelChange || null,
          forecastDate: employeeTableFormValues[employee.employeeId].currentValue.forecastDate || null,
          forecastEndDate: employeeTableFormValues[employee.employeeId].currentValue.forecastEndDate || null,
        })
      }
    }
    return acc
  }, [])

  const oldHiringsToDeleteOrUpdate = employeeData
    .filter((hiring: PyramidForecastDto) => !hiring.employeeId)
    .reduce((acc: PyramidForecastInput[], oldHiring: PyramidForecastDto) => {
      if (deletedHirings.includes(oldHiring.id)) {
        acc.push({
          id: oldHiring.id,
          forecastId: oldHiring.forecastId,
          employeeId: null,
          levelChange: null,
          forecastEndDate: null,
          companyId,
          country,
        })
      } else if (!!hiringsData.length) {
        const hiring = hiringsData.find((hiring) => oldHiring.id === hiring.id)
        if (hiring) {
          if (!compareHiringsHash(oldHiring, hiring)) {
            const { isNewHiring, key, ...rest } = hiring
            acc.push({ employeeId: null, forecastId: oldHiring.forecastId, companyId, country, ...rest })
          }
        }
      }
      return acc
    }, [])

  const newHirings: PyramidForecastInput[] = hiringsData
    .filter((hiring) => !!hiring.isNewHiring && !!hiring.levelChange)
    .map((hiring) => {
      const { id, isNewHiring, key, ...rest } = hiring
      return { ...rest, companyId, country }
    })

  pyramidForecastInput = [...pyramidForecastInput, ...oldHiringsToDeleteOrUpdate, ...newHirings]

  return pyramidForecastInput
}

export const filterForecastModalEmployees = (
  data: PyramidForecastDto[],
  currentFilters: ForecastModalFilterValues,
): PyramidForecastDto[] => {
  if (!data || !data.length) {
    return []
  }

  if (!Object.keys(currentFilters).length) {
    return data
  }

  const filteredData = data.filter((item) => {
    for (const key in currentFilters) {
      if (key === 'careerPath' && !!item.careerPath && item.careerPath.summary === currentFilters[key]) {
        return true
      } else if (key === 'tags' && !!item.tags && !!item.tags.length && item.tags.some((tag) => tag === currentFilters[key])) {
        return true
      } else if (item[key] === currentFilters[key]) {
        return true
      }
    }
    return false
  })

  return filteredData
}

export const setForecastModalEmployeeErrors = (
  row: IForecastModalEmployeeTableField,
  field: ForecastModalEmployeeTableFields,
  value: string,
): ForecastModalEmployeeTableFields => {
  switch (field) {
    case 'forecastDate': {
      if (!value) {
        return !row.levelChange ? '' : 'forecastDate'
      }
      return !!row.levelChange ? '' : 'levelChange'
    }
    case 'levelChange': {
      if (!value) {
        return !row.forecastDate ? '' : 'levelChange'
      }
      return !!row.forecastDate ? '' : 'forecastDate'
    }
    default: {
      return ''
    }
  }
}

const checkForActiveEmployeeChanges = (employee: PyramidForecastDto, formValues: IForecastModalEmployeeTableForm) => {
  const hasForecast = !!employee.levelChange && !!employee.forecastDate && !!employee.forecastEndDate
  const hasRowChanges =
    !!formValues[employee.employeeId].currentValue.levelChange ||
    !!formValues[employee.employeeId].currentValue.forecastDate ||
    !!formValues[employee.employeeId].currentValue.forecastEndDate
  const hasEmptyRow =
    formValues[employee.employeeId].currentValue.levelChange === null && formValues[employee.employeeId].currentValue.forecastDate === null

  return hasForecast ? !hasEmptyRow : hasRowChanges
}

export const checkForecastModalEmployeeChanges = (
  employees: PyramidForecastDto[],
  formValues: IForecastModalEmployeeTableForm,
  selectedRows: string[],
): boolean => {
  return employees
    .filter((employee) => selectedRows.includes(employee.employeeId))
    .some((employee) => checkForActiveEmployeeChanges(employee, formValues))
}

export const checkForecastModalEmployeeRowChange = (
  employeeId: string,
  employees: PyramidForecastDto[],
  formValues: IForecastModalEmployeeTableForm,
): boolean => {
  return employees
    .filter((employee) => employee.employeeId === employeeId)
    .some((employee) => checkForActiveEmployeeChanges(employee, formValues))
}
